import { useEffect, useMemo } from 'react';
import { Auth0Provider, AppState, Auth0Context, Auth0ContextInterface } from '@auth0/auth0-react';
import type { Auth0Context as Auth0ContextType, Env, ILogger } from '@bloomays-lib/types.shared';

import { AuthProvider } from '../providers/AuthProvider';
import { type ApolloClientConfig } from '../types';
import { createApolloStore } from '../stores/apolloStore';
import BloomaysApolloProvider, { type BloomaysApolloProviderProps } from './BloomaysApolloProvider';

type AuthApolloProviderProps = {
  children: React.ReactNode;
  loaderAuthComponent: React.ReactNode;
  mockAuth0Context?: Auth0ContextType;
  auth0ProviderConfig: Auth0ProviderConfig;
  logger: ILogger;
  env: Env;
  apolloStoreConfig: ApolloClientConfig;
};

export type Auth0ProviderConfig = {
  domain: string;
  clientId: string;
  audience?: string;
};

export const MockAuth0Provider = ({
  children,
  mockContext,
}: {
  children: React.ReactNode;
  mockContext: Auth0ContextType;
}) => {
  const mockedMemo = useMemo(() => {
    return {
      user: mockContext.user,
      isAuthenticated: mockContext.isAuthenticated,
      isLoading: mockContext.isLoading,
      getAccessTokenSilently: mockContext.getAccessTokenSilently as Auth0ContextInterface['getAccessTokenSilently'],
      getAccessTokenWithPopup: () => Promise.resolve(undefined),
      getIdTokenClaims: () => Promise.resolve(undefined),
      loginWithRedirect: mockContext.loginWithRedirect,
      loginWithPopup: () => Promise.resolve(undefined),
      logout: mockContext.logout,
      handleRedirectCallback: () => Promise.resolve({ appState: '/' }),
    };
  }, [mockContext]);

  return (
    <Auth0Context.Provider key={mockContext.isAuthenticated ? 'auth' : 'unauth'} value={mockedMemo}>
      {children}
    </Auth0Context.Provider>
  );
};

const BloomaysAuthApolloProvider = ({
  children,
  logger,
  loaderAuthComponent,
  auth0ContextInterface,
  env,
  useApolloStore,
}: AuthApolloProviderProps & {
  auth0ContextInterface: Auth0ContextType;
  useApolloStore: BloomaysApolloProviderProps['useApolloStore'];
}) => {
  return (
    <AuthProvider auth0Context={auth0ContextInterface} loaderAuthComponent={loaderAuthComponent}>
      <BloomaysApolloProvider
        env={env}
        useApolloStore={useApolloStore}
        loaderComponent={loaderAuthComponent}
        logger={logger}
      >
        {children}
      </BloomaysApolloProvider>
    </AuthProvider>
  );
};

export const AuthApolloProvider = ({
  children,
  mockAuth0Context,
  auth0ProviderConfig,
  logger,
  loaderAuthComponent,
  apolloStoreConfig,
  env,
}: AuthApolloProviderProps) => {
  const onRedirectCallback = (appState?: AppState) => {
    window.location.replace(appState?.returnTo || '/');
  };

  useEffect(() => {
    logger?.debug('mockAuth0Context change', mockAuth0Context);
  }, [mockAuth0Context, logger]);

  const useApolloStore = createApolloStore({
    apiUrls: {
      bloomers: apolloStoreConfig.apiUrls.bloomers,
      talents: apolloStoreConfig.apiUrls.talents,
    },
    logger,
    typePolicies: apolloStoreConfig.typePolicies,
    filterExceptions: apolloStoreConfig.filterExceptions,
  });

  if (mockAuth0Context) {
    return (
      <MockAuth0Provider mockContext={mockAuth0Context}>
        <Auth0Context.Consumer>
          {(auth0ContextInterface: Auth0ContextInterface) => {
            return (
              <BloomaysAuthApolloProvider
                auth0ProviderConfig={auth0ProviderConfig}
                apolloStoreConfig={apolloStoreConfig}
                auth0ContextInterface={auth0ContextInterface as Auth0ContextType}
                useApolloStore={useApolloStore}
                loaderAuthComponent={loaderAuthComponent}
                env={env}
                logger={logger}
                children={children}
              />
            );
          }}
        </Auth0Context.Consumer>
      </MockAuth0Provider>
    );
  }

  return (
    <Auth0Provider
      domain={auth0ProviderConfig.domain}
      clientId={auth0ProviderConfig.clientId}
      authorizationParams={{
        audience: auth0ProviderConfig.audience,
        redirect_uri: window.location.origin,
        scope: 'openid profile email',
      }}
      onRedirectCallback={onRedirectCallback}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensFallback={true}
    >
      <Auth0Context.Consumer>
        {(auth0ContextInterface: Auth0ContextInterface) => {
          return (
            <BloomaysAuthApolloProvider
              auth0ProviderConfig={auth0ProviderConfig}
              apolloStoreConfig={apolloStoreConfig}
              auth0ContextInterface={auth0ContextInterface as Auth0ContextType}
              useApolloStore={useApolloStore}
              loaderAuthComponent={loaderAuthComponent}
              env={env}
              logger={logger}
              children={children}
            />
          );
        }}
      </Auth0Context.Consumer>
    </Auth0Provider>
  );
};
